import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexto/AuthContexto';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, Car, ClipboardList, CreditCard, Settings,
  LogOut, Menu, X, CalendarDays, Bell, UserCog, Calculator, Droplets,
  ListOrdered, Tag, Wallet, ShoppingBag, AlertCircle, Crown, Mail, Link2, Package,
  Sun, Moon, Calendar, Building2, Sparkles
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import AyudaWhatsApp from '@/componentes/AyudaWhatsApp';
import TourSistema from '@/componentes/TourSistema';

const menuAdmin = [
  { href: '/admin', label: 'Panel Admin', icon: LayoutDashboard },
];

const menuTaller = [
  { href: '/dashboard', label: 'Inicio', icon: LayoutDashboard, roles: ['propietario', 'administrativo'] },
  { href: '/clientes', label: 'Clientes', icon: Users, roles: ['propietario', 'administrativo'] },
  { href: '/vehiculos', label: 'Vehiculos', icon: Car, roles: ['propietario', 'administrativo'] },
  { href: '/ordenes', label: 'Ordenes', icon: ClipboardList, roles: ['propietario', 'administrativo', 'mecanico'] },
  { href: '/pagos', label: 'Pagos', icon: CreditCard, roles: ['propietario', 'administrativo'] },
  { href: '/corte-caja', label: 'Corte de Caja', icon: Wallet, roles: ['propietario', 'administrativo'] },
  { href: '/citas', label: 'Citas', icon: CalendarDays, roles: ['propietario', 'administrativo'], feature: 'agenda_publica' },
  { href: '/recordatorios', label: 'Recordatorios', icon: Bell, roles: ['propietario', 'administrativo'] },
  { href: '/staff', label: 'Staff', icon: UserCog, roles: ['propietario', 'administrativo'], feature: 'staff_multiple' },
  { href: '/contabilidad', label: 'Contabilidad', icon: Calculator, roles: ['propietario', 'administrativo'] },
  { href: '/cierres', label: 'Cierres', icon: Calendar, roles: ['propietario', 'administrativo'], feature: 'cierres_contables' },
  { href: '/inventario', label: 'Inventario', icon: Package, roles: ['propietario', 'administrativo'], feature: 'inventario' },
  { href: '/novedades', label: 'Novedades', icon: Bell, roles: ['propietario', 'administrativo', 'mecanico'] },
  { href: '/configuracion', label: 'Configuracion', icon: Settings, roles: ['propietario'] },
  { href: '/corporativo', label: 'Corporativo', icon: Building2, roles: ['propietario'], feature: 'multi_sucursal' },
  { href: '/complementos', label: 'Complementos', icon: Sparkles, roles: ['propietario'] },
  { href: '/suscripcion', label: 'Mi Suscripcion', icon: Crown, roles: ['propietario'] },
];

const menuCarwash = [
  { href: '/carwash', label: 'Inicio', icon: LayoutDashboard, roles: ['propietario', 'administrativo'] },
  { href: '/carwash/turnos', label: 'Turnos', icon: ListOrdered, roles: ['propietario', 'administrativo', 'cajero'] },
  { href: '/carwash/citas', label: 'Citas', icon: CalendarDays, roles: ['propietario', 'administrativo', 'cajero'], feature: 'agenda_publica' },
  { href: '/carwash/clientes', label: 'Clientes', icon: Users, roles: ['propietario', 'administrativo', 'cajero'] },
  { href: '/carwash/servicios', label: 'Servicios', icon: Tag, roles: ['propietario', 'administrativo'] },
  { href: '/carwash/mostrador', label: 'Mostrador', icon: ShoppingBag, roles: ['propietario', 'administrativo', 'cajero'] },
  { href: '/carwash/caja', label: 'Caja del Dia', icon: Wallet, roles: ['propietario', 'administrativo', 'cajero'] },
  { href: '/carwash/staff', label: 'Staff', icon: UserCog, roles: ['propietario', 'administrativo'], feature: 'staff_multiple' },
  { href: '/carwash/agenda', label: 'Liga Agenda', icon: Link2, roles: ['propietario', 'administrativo'], feature: 'agenda_publica' },
  { href: '/carwash/nomina', label: 'Nomina', icon: UserCog, roles: ['propietario', 'administrativo'], feature: 'nomina' },
  { href: '/carwash/contabilidad', label: 'Contabilidad', icon: Calculator, roles: ['propietario', 'administrativo'] },
  { href: '/carwash/cierres', label: 'Cierres', icon: Calendar, roles: ['propietario', 'administrativo'], feature: 'cierres_contables' },
  { href: '/carwash/inventario', label: 'Inventario', icon: Package, roles: ['propietario', 'administrativo', 'cajero'], feature: 'inventario' },
  { href: '/novedades', label: 'Novedades', icon: Bell, roles: ['propietario', 'administrativo', 'cajero'] },
  { href: '/configuracion', label: 'Configuracion', icon: Settings, roles: ['propietario'] },
  { href: '/corporativo', label: 'Corporativo', icon: Building2, roles: ['propietario'], feature: 'multi_sucursal' },
  { href: '/complementos', label: 'Complementos', icon: Sparkles, roles: ['propietario'], soloSiTiendaVisible: true },
  { href: '/suscripcion', label: 'Mi Suscripcion', icon: Crown, roles: ['propietario'] },
];

export default function Layout({ children }) {
  const { usuario, suscripcion, cerrarSesion, featuresPlan, tiendaVisible, cargarFeatures } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [noLeidas, setNoLeidas] = useState(0);
  const [modoClaro, setModoClaro] = useState(() => localStorage.getItem('theme') === 'light');

  useEffect(() => {
    if (modoClaro) {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
    localStorage.setItem('theme', modoClaro ? 'light' : 'dark');
  }, [modoClaro]);

  const esAdmin = usuario?.rol === 'super_admin';
  const esCarwash = usuario?.tipo_negocio === 'carwash';
  const rolUsuario = usuario?.rol || 'propietario';
  const menuBase = esAdmin ? menuAdmin : esCarwash ? menuCarwash : menuTaller;

  // Recargar features del plan en cada cambio de ruta (para reflejar cambios del admin sin re-login)
  useEffect(() => {
    if (esAdmin || !usuario) return;
    cargarFeatures();
  }, [location.pathname, esAdmin, usuario, cargarFeatures]);

  const menu = menuBase.filter(item => {
    if (item.roles && !item.roles.includes(rolUsuario)) return false;
    // feature gating: si el item tiene .feature y el plan no la incluye → ocultar
    if (item.feature && featuresPlan !== null && !featuresPlan.includes(item.feature)) return false;
    // Tienda de complementos oculta por defecto — el super admin la activa desde admin panel
    if (item.soloSiTiendaVisible && !tiendaVisible) return false;
    return true;
  });

  const diasRestantes = suscripcion?.vence
    ? Math.ceil((new Date(suscripcion.vence) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  // Load unread notifications count
  useEffect(() => {
    if (!esAdmin) {
      import('@/lib/api').then(({ default: api }) => {
        api.get('/notificaciones/no-leidas').then(r => setNoLeidas(r.data.no_leidas)).catch(() => {});
      });
    }
  }, [esAdmin, location.pathname]);

  const handleLogout = () => {
    cerrarSesion();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900/80 border-r border-slate-800/50 flex flex-col transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-5 border-b border-slate-800/50">
          <div className="flex items-center gap-2.5">
            {esAdmin ? (
              <div className="w-9 h-9 bg-amber-500/20 rounded-sm flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </div>
            ) : esCarwash ? (
              <div className="w-9 h-9 bg-cyan-500/20 rounded-sm flex items-center justify-center">
                <Droplets className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
              </div>
            ) : (
              <div className="w-9 h-9 bg-amber-500/20 rounded-sm flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </div>
            )}
            <div>
              <h1 className={`font-barlow text-lg font-bold uppercase tracking-wider ${esCarwash && !esAdmin ? 'text-cyan-400' : 'text-amber-500'}`}>
                {esAdmin ? 'Talleres PRO' : esCarwash ? 'WashPro' : 'Talleres PRO'}
              </h1>
              <p className="text-[10px] text-slate-600 uppercase tracking-widest">
                {esAdmin ? 'Administración' : esCarwash ? 'Car Wash' : 'Gestión Automotriz'}
              </p>
            </div>
          </div>
          {usuario && (
            <div className="mt-4 px-2 py-2 bg-slate-800/40 rounded-sm">
              <p className="text-xs text-slate-400 truncate">{usuario.tenant_nombre}</p>
              <p className="text-[10px] text-slate-600 truncate mt-0.5">{usuario.email}</p>
            </div>
          )}
        </div>

        {/* Subscription warning */}
        {!esAdmin && diasRestantes !== null && diasRestantes <= 7 && diasRestantes >= 0 && (
          <div className="mx-3 mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded-sm">
            <p className="text-[10px] text-amber-400 font-medium">Tu suscripción vence en {diasRestantes} día(s)</p>
          </div>
        )}

        {/* Grace period banner: post-vencimiento pero antes del bloqueo */}
        {!esAdmin && suscripcion?.estado === 'en_gracia' && (
          <div className="mx-3 mt-3 p-2.5 bg-red-500/10 border border-red-500/40 rounded-sm" data-testid="gracia-banner">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] text-red-300 leading-tight font-medium">
                  Tu suscripción venció. Te damos {suscripcion.dias_gracia_restantes ?? 0} día(s) más para procesar el pago.
                </p>
                <Link to="/suscripcion" className="text-[10px] text-red-400 hover:text-red-300 underline mt-1 inline-block">
                  Renovar ahora →
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Email verification banner */}
        {!esAdmin && !usuario?.email_verificado && (
          <div className="mx-3 mt-3 p-2.5 bg-cyan-500/10 border border-cyan-500/30 rounded-sm" data-testid="verificar-correo-banner">
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] text-cyan-300 leading-tight">Verifica tu correo para activar todas las funciones</p>
                <button
                  className="text-[10px] text-cyan-400 hover:text-cyan-300 underline mt-1"
                  data-testid="reenviar-verificacion-btn"
                  onClick={async () => {
                    try {
                      await api.post('/auth/enviar-verificacion');
                      toast.success('Correo de verificacion enviado. Revisa tu bandeja.');
                    } catch { toast.error('Error al enviar verificacion'); }
                  }}>
                  Reenviar correo de verificacion
                </button>
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {menu.map(item => {
            const Icon = item.icon;
            const activo = location.pathname === item.href || (item.href !== '/dashboard' && item.href !== '/carwash' && location.pathname.startsWith(item.href));
            const accentColor = esCarwash && !esAdmin ? 'bg-cyan-500/15 text-cyan-400' : 'bg-amber-500/15 text-amber-400';
            const barColor = esCarwash && !esAdmin ? 'bg-cyan-500' : 'bg-amber-500';
            return (
              <Link key={item.href} to={item.href} onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all relative ${activo ? `${accentColor} font-medium` : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/40'}`}
                data-testid={`nav-${item.href.replace(/\//g, '-').slice(1) || 'home'}`}>
                {activo && <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 ${barColor} rounded-r`} />}
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
                {item.href === '/novedades' && noLeidas > 0 && (
                  <span className="ml-auto bg-amber-500 text-black text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
                    data-testid="novedades-badge">{noLeidas}</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-800/50">
          <button onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-slate-500 hover:text-red-400 hover:bg-red-500/10 w-full transition-colors"
            data-testid="cerrar-sesion-btn">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Cerrar Sesion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/50 px-4 py-3 flex items-center justify-between lg:justify-end">
          <button className="lg:hidden text-slate-400 hover:text-white" onClick={() => setSidebarOpen(true)} data-testid="menu-toggle-btn">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <button onClick={() => {
                const nuevo = !modoClaro;
                setModoClaro(nuevo);
                if (nuevo) { document.documentElement.classList.add('light'); } else { document.documentElement.classList.remove('light'); }
                localStorage.setItem('theme', nuevo ? 'light' : 'dark');
              }}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-slate-800/50"
              data-testid="theme-toggle-btn"
              title={modoClaro ? 'Modo nocturno' : 'Modo claro'}>
              {modoClaro ? <Moon className="w-4 h-4 text-slate-500" /> : <Sun className="w-4 h-4 text-amber-400" />}
            </button>
            {usuario && (
              <Badge variant="outline" className={`text-xs ${esCarwash && !esAdmin ? 'text-cyan-400 border-cyan-800' : 'text-amber-400 border-amber-800'}`} data-testid="user-badge">
                {usuario.nombre}
              </Badge>
            )}
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl">
          {children}
        </main>
      </div>
      <AyudaWhatsApp />
      <TourSistema />
    </div>
  );
}
